/**
 * RichMessageText Component
 *
 * Renders text content with embedded [MEDIA:*] tags replaced by actual media.
 * Reuses existing MediaPreview component for consistent display and lightbox support.
 */

import React from 'react';
import { parseMediaTags, normalizeFilename } from '@/utils/mediaTagParser';
import { MediaPreview, MediaItem } from './MediaPreview';

interface RichMessageTextProps {
  text: string;
  uploadedMedia?: Array<{ filename: string; url: string; type: string }>;
  className?: string;
}

export const RichMessageText: React.FC<RichMessageTextProps> = ({
  text,
  uploadedMedia = [],
  className = ''
}) => {
  const parsed = parseMediaTags(text);

  // Create a map of normalized filename -> media item (Phase 3.5)
  // Normalizes filenames to handle spacing issues for reliable matching
  const mediaMap = new Map(
    uploadedMedia.map(m => ({
      filename: normalizeFilename(m.filename),  // Normalize for consistent matching
      mediaItem: {
        type: m.type as 'image' | 'video',
        url: m.url
      } as MediaItem
    })).map(item => [item.filename, item.mediaItem])
  );

  return (
    <div className={className}>
      {parsed.segments.map((segment, index) => {
        if (segment.type === 'text') {
          // Render text with preserved whitespace and line breaks
          return (
            <span key={`text-${index}`} className="whitespace-pre-wrap">
              {segment.content}
            </span>
          );
        } else {
          // Render media using existing MediaPreview component
          const tag = segment.mediaTag!;
          const mediaItem = mediaMap.get(tag.filename);

          if (!mediaItem) {
            // Media not found, show placeholder
            return (
              <span
                key={`media-${index}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-inset dark:bg-card-darkAlt text-ink-2 rounded text-sm my-1"
                title={`Media: ${tag.filename}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs">{tag.filename}</span>
              </span>
            );
          }

          // Render with placement-specific wrapper
          const mediaDisplay = <MediaPreview key={`media-${index}`} media={[mediaItem]} />;

          switch (tag.placement) {
            case 'hero':
              // Large, block display
              return (
                <div key={`media-${index}`} className="my-4 max-w-2xl">
                  {mediaDisplay}
                </div>
              );
            case 'carousel':
            case 'feed':
              // Medium display for carousel and feed placements
              return (
                <div key={`media-${index}`} className="my-3 max-w-lg">
                  {mediaDisplay}
                </div>
              );
            case 'story':
              // Vertical story format - narrower
              return (
                <div key={`media-${index}`} className="my-3 max-w-sm">
                  {mediaDisplay}
                </div>
              );
            case 'inline':
              // Inline with text, medium size
              return (
                <div key={`media-${index}`} className="inline-block my-2 mx-2 max-w-md">
                  {mediaDisplay}
                </div>
              );
            case 'thumbnail':
              // Small inline thumbnail
              return (
                <span key={`media-${index}`} className="inline-block mx-1 align-middle">
                  {mediaDisplay}
                </span>
              );
            case 'background':
              // Preview with background indicator
              return (
                <div key={`media-${index}`} className="inline-block my-2 mx-2 relative">
                  <div className="opacity-60">
                    {mediaDisplay}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-white text-xs font-medium px-2 py-1 bg-black/50 rounded backdrop-blur-sm">
                      Background
                    </span>
                  </div>
                </div>
              );
            default:
              return mediaDisplay;
          }
        }
      })}
    </div>
  );
};

export default RichMessageText;
